import { FC, useContext, useLayoutEffect, useRef, useState } from 'react';

import { Label, Separator } from '@smartshell/ui-kit';
import { truncate } from 'lodash';
import { createPortal } from 'react-dom';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { IPaymentHistoryItem } from 'types';

import s from './PaymentItemsTooltip.module.scss';
import { formatSum, getCategoryConfig, getIcon } from './utils';

interface IPaymentItemsTooltipProps {
  items: IPaymentHistoryItem['items'];
}
export const PaymentItemsTooltip: FC<IPaymentItemsTooltipProps> = ({
  items,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  const openerRef = useRef<HTMLSpanElement>(null);
  const [tooltipContainerEl, setTooltipContainerEl] =
    useState<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    if (!isVisible || !openerRef.current) return;

    const { top, left } = openerRef.current.getBoundingClientRect();

    const el = document.createElement('div');
    el.classList.add(s.tooltip);
    el.style.top = `${top - 8}px`;
    el.style.left = `${left - 8}px`;

    document.body.appendChild(el);
    setTooltipContainerEl(el);

    return () => {
      document.body.removeChild(el);
    };
  }, [isVisible, openerRef.current]);

  useLayoutEffect(() => {
    if (!tooltipContainerEl) return;

    const { top, height } = tooltipContainerEl.getBoundingClientRect();

    if (window.innerHeight <= top + height) {
      tooltipContainerEl.style.top = `${window.innerHeight - height - 8}px`;
    }
  }, [tooltipContainerEl]);

  const categoryConfig = getCategoryConfig(items);

  if (!categoryConfig) return null;

  return (
    <span
      ref={openerRef}
      className={s.category}
      onMouseEnter={() => {
        if (tooltipTimer.current) {
          clearTimeout(tooltipTimer.current);
          tooltipTimer.current = null;
        }

        setIsVisible(true);
      }}
      onMouseLeave={() => {
        tooltipTimer.current = setTimeout(() => setIsVisible(false), 50);
      }}
    >
      {t(categoryConfig.locale)}
      {tooltipContainerEl &&
        createPortal(
          <div className={s.tooltipContent}>
            <div className={s.title}>
              <Label iconName={categoryConfig?.iconName} color="gray-primary" />
              {t(categoryConfig.locale)}
            </div>
            <Separator />
            {items.map((item) => {
              const ItemIcon = getIcon(item.type);

              if (item.type === 'DEPOSIT') {
                return (
                  <div key={`payment-item-${item.id}`} className={s.row}>
                    <div>{t('payments.deposit.sum')}</div>
                    <span className={s.sum}>
                      {formatSum(item.sum)} {currency}
                    </span>
                  </div>
                );
              }

              if (item.type === 'BONUS') {
                return (
                  <div key={`payment-item-${item.id}`} className={s.row}>
                    <div>{t('payments.deposit.sum')}</div>
                    <span className={s.sum}>{formatSum(item.sum)}</span>
                  </div>
                );
              }

              const cost: number = item.sum / item.amount;

              return (
                <div key={`payment-item-${item.id}`} className={s.row}>
                  {categoryConfig?.multi && (
                    <ItemIcon size="s" color="secondary" />
                  )}
                  <div className={s.info}>
                    {truncate(item.entity.title, {
                      length: 17,
                      separator: '...',
                    })}
                    {!isNaN(cost) && (
                      <span className={s.cost}>
                        {formatSum(cost)} {currency} x {item.amount} {t('pcs')}
                      </span>
                    )}
                  </div>
                  <span className={s.sum}>
                    {formatSum(item.sum)} {currency}
                  </span>
                </div>
              );
            })}
          </div>,
          tooltipContainerEl
        )}
    </span>
  );
};
